import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatPHP, formatDate, formatErrorDetail, API_BASE } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import { Plus, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function PayrollRuns() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const today = new Date();
    const yy = today.getFullYear(); const mm = String(today.getMonth() + 1).padStart(2, "0");
    const [form, setForm] = useState({ period_start: `${yy}-${mm}-01`, period_end: `${yy}-${mm}-15`, frequency: "semi_monthly", run_type: "regular", off_cycle_reason: "" });

    const load = async () => {
        const { data } = await api.get("/payroll/runs");
        setItems(data.items || []);
    };
    useEffect(() => { load(); }, []);

    const isOffCycle = form.run_type !== "regular";

    const create = async () => {
        if (isOffCycle && !form.off_cycle_reason.trim()) return toast.error("A reason is required for off-cycle runs");
        try {
            const r = await api.post("/payroll/runs", form);
            toast.success(isOffCycle
                ? "Off-cycle run created"
                : "Cutoff created - Basic and Allowance runs");
            setOpen(false);
            navigate(`/payroll/runs/${r.data.id}`);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div>
            <PageHeader
                title="Payroll Runs"
                subtitle="Semi-monthly payroll cycles. Compute, review, approve through HR → Accounting → GM, then lock with immutable snapshot."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="new-run-btn"><Plus className="size-4" /> New Run</button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>New Payroll Run</DialogTitle></DialogHeader>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Period Start</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="run-start" /></div>
                                <div><Label>Period End</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="run-end" /></div>
                                <div className="col-span-2">
                                    <Label>Frequency</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="run-frequency">
                                        <option value="semi_monthly">Semi-monthly</option>
                                        <option value="monthly">Monthly</option>
                                        <option value="weekly">Weekly</option>
                                    </select>
                                </div>
                                <div className="col-span-2">
                                    <Label>Run Type</Label>
                                    <select className="w-full h-10 px-3 rounded-md border border-border" value={form.run_type} onChange={(e) => setForm({ ...form, run_type: e.target.value })} data-testid="run-type">
                                        <option value="regular">Regular</option>
                                        <option value="bonus">Off-cycle · Bonus</option>
                                        <option value="commission">Off-cycle · Commission</option>
                                        <option value="adjustment">Off-cycle · Adjustment</option>
                                        <option value="emergency">Off-cycle · Emergency</option>
                                        <option value="separation">Off-cycle · Separation</option>
                                        <option value="special">Off-cycle · Special</option>
                                    </select>
                                </div>
                                {isOffCycle && (
                                    <div className="col-span-2">
                                        <Label>Reason for off-cycle run</Label>
                                        <Input value={form.off_cycle_reason} onChange={(e) => setForm({ ...form, off_cycle_reason: e.target.value })} placeholder="e.g. Mid-year performance bonus" data-testid="run-off-cycle-reason" />
                                    </div>
                                )}
                                <div><Label>Payout Date</Label><Input type="date" value={form.payout_date || ""} onChange={(e) => setForm({ ...form, payout_date: e.target.value })} data-testid="run-payout" /></div>
                            </div>
                            <DialogFooter>
                                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={create} data-testid="run-create-submit">Create Run</button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <TableWrap>
                <thead>
                    <tr>
                        <th>Period</th>
                        <th>Frequency</th>
                        <th>Status</th>
                        <th className="num">Employees</th>
                        <th className="num">Gross</th>
                        <th className="num">Deductions</th>
                        <th className="num">Net Pay</th>
                        <th>Lock</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((r) => (
                        <tr key={r.id} className="cursor-pointer" onClick={() => navigate(`/payroll/runs/${r.id}`)} data-testid={`run-row-${r.id}`}>
                            <td className="font-mono">{r.period_start} → {r.period_end}{r.run_type === "allowance"
                                ? <span className="badge badge-warning ml-2" title="Allowances only - no tax or government contributions">Allowance</span>
                                : r.run_type && r.run_type !== "regular"
                                ? <span className="badge badge-info ml-2 capitalize" title={r.off_cycle_reason || ""}>{r.run_type}</span>
                                : <span className="badge ml-2" title="Basic salary, statutory contributions and withholding tax">Basic</span>}</td>
                            <td className="capitalize">{r.frequency?.replace("_", " ")}</td>
                            <td><StatusBadge value={r.status} /></td>
                            <td className="num">{r.employee_count}</td>
                            <td className="num">{formatPHP(r.total_gross)}</td>
                            <td className="num">{formatPHP(r.total_deductions)}</td>
                            <td className="num font-semibold">{formatPHP(r.total_net)}</td>
                            <td>{r.locked ? <StatusBadge value="locked" /> : <span className="badge badge-info">unlocked</span>}</td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={8}><Empty title="No payroll runs" subtitle="Create the first run for this company." /></td></tr>}
                </tbody>
            </TableWrap>
        </div>
    );
}
